/**
 * Math editor logger + in-memory log buffer.
 *
 * One thin layer over `console` that:
 *   - Prefixes every line with `[math/<scope>]` so the user can grep
 *     a single subsystem in the browser console.
 *   - Mirrors every entry into a bounded ring buffer that the
 *     DevTools panel reads from to display a live transition stream.
 *   - Exposes a `subscribe()` channel so the panel can react to new
 *     entries without polling.
 *
 * The buffer cap (`MAX_BUFFER`) is deliberately small — this is a
 * diagnosis tool, not an audit log. Older entries fall off.
 *
 * Console output is gated on `lilia.math.logs` localStorage flag.
 * The DevTools panel always reads from the buffer, regardless of the
 * console flag. That way the user can keep the console quiet but
 * still inspect history when something looks wrong.
 */

const STORAGE_FLAG = "lilia.math.logs";
const MAX_BUFFER = 300;

/**
 * localStorage key for the persisted ring buffer. We snapshot the
 * buffer to localStorage on a debounced schedule and rehydrate on
 * module load — so a forced reload mid-debug doesn't erase the
 * trail. Entries older than RETENTION_MS are dropped on rehydrate
 * so the buffer can't grow unbounded across days.
 */
const BUFFER_KEY = "lilia.math.logs.buffer";
const RETENTION_MS = 24 * 60 * 60 * 1000;
/**
 * Debounce window for localStorage writes. Editor inserts fire
 * dozens of log events per second under heavy typing; persisting
 * every push synchronously would block the keystroke. 750ms is
 * tight enough that a reload-after-bug captures the last ~750ms of
 * activity, loose enough that we don't hammer storage on idle.
 */
const PERSIST_DEBOUNCE_MS = 750;

export type LogLevel = "info" | "warn" | "error" | "debug";

export type LogScope =
  | "init"
  | "state"
  | "insert"
  | "backspace"
  | "caret"
  | "slot"
  | "save"
  | "autosave"
  | "load"
  | "library"
  | "done"
  | "host"
  // Document-level sync scopes — emitted by `useDocumentSync.ts`.
  // Grouped under `sync*` so the DevTools panel filter can isolate
  // them from editor-local entries.
  | "sync"        // high-level sync state transitions
  | "sync-tick"   // each polling iteration: skip / push / done
  | "rebase";     // 409 → fetch server → merge → bump editVersion

export interface LogEntry {
  /** Monotonic counter so the panel can use it as a React key. */
  seq: number;
  /** Unix ms — used by the panel to show elapsed deltas. */
  ts: number;
  scope: LogScope;
  level: LogLevel;
  message: string;
  /** Free-form, JSON-serialisable payload. */
  data?: unknown;
}

let seq = 0;
const buffer: LogEntry[] = [];
const subscribers = new Set<(entry: LogEntry, buf: LogEntry[]) => void>();

// ── Persistence ─────────────────────────────────────────────────────
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersist(): void {
  if (typeof window === "undefined") return;
  if (persistTimer != null) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      // Persist only the most-recent entries — saves us from
      // re-serializing the full ring every push, and matches what the
      // panel actually shows on rehydrate.
      window.localStorage.setItem(BUFFER_KEY, JSON.stringify(buffer));
    } catch {
      // Quota exceeded / Safari private mode / dev tools wiping
      // storage — none of these are fatal. The in-memory buffer
      // keeps working; we just lose the cross-reload trail.
    }
  }, PERSIST_DEBOUNCE_MS);
}

/**
 * Rehydrate the buffer from localStorage on module load. Drops
 * anything older than RETENTION_MS so a stale tab opened next week
 * doesn't fill the panel with last-week's noise. Resets `seq` to
 * the max persisted value so new entries don't collide on react
 * keys.
 *
 * Called once at module evaluation — safe to call again if the
 * caller wants to force a re-read (rare).
 */
function rehydrate(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(BUFFER_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as LogEntry[];
    if (!Array.isArray(parsed) || parsed.length === 0) return;
    const cutoff = Date.now() - RETENTION_MS;
    const valid = parsed.filter((e) =>
      e && typeof e.ts === "number" && e.ts >= cutoff,
    );
    if (valid.length === 0) return;
    buffer.push(...valid);
    // Trim if rehydration overflowed the in-memory cap. Keep the
    // newest entries (the buffer's "right side").
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    seq = valid.reduce((m, e) => (e.seq > m ? e.seq : m), 0);
  } catch {
    // Corrupt JSON / wrong shape — just start fresh. We don't want
    // a broken localStorage entry to crash the logger.
  }
}

rehydrate();

function consoleEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_FLAG) === "1";
  } catch {
    return false;
  }
}

function push(level: LogLevel, scope: LogScope, message: string, data?: unknown): void {
  const entry: LogEntry = {
    seq: ++seq,
    ts: Date.now(),
    scope,
    level,
    message,
    data,
  };
  buffer.push(entry);
  if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
  schedulePersist();

  if (consoleEnabled()) {
    const tag = `[math/${scope}]`;
    const args = data === undefined ? [tag, message] : [tag, message, data];
    switch (level) {
      case "error": console.error(...args); break;
      case "warn":  console.warn(...args); break;
      case "debug": console.debug(...args); break;
      default:      console.log(...args);
    }
  }

  // Snapshot the buffer so subscribers can't accidentally mutate it.
  const snap = buffer.slice();
  for (const sub of subscribers) {
    try { sub(entry, snap); } catch { /* swallow subscriber errors */ }
  }
}

export const log = {
  info:  (scope: LogScope, message: string, data?: unknown) => push("info",  scope, message, data),
  warn:  (scope: LogScope, message: string, data?: unknown) => push("warn",  scope, message, data),
  error: (scope: LogScope, message: string, data?: unknown) => push("error", scope, message, data),
  debug: (scope: LogScope, message: string, data?: unknown) => push("debug", scope, message, data),
};

/** Subscribe to log entries. Returns an unsubscribe function. */
export function subscribeLogs(
  cb: (entry: LogEntry, buf: LogEntry[]) => void,
): () => void {
  subscribers.add(cb);
  // Immediately hand the subscriber the current buffer so it can
  // bootstrap without waiting for the next entry.
  if (buffer.length > 0) {
    try { cb(buffer[buffer.length - 1], buffer.slice()); } catch { /* */ }
  }
  return () => subscribers.delete(cb);
}

/** Snapshot of the current ring buffer. Safe to mutate the result. */
export function snapshotLogs(): LogEntry[] {
  return buffer.slice();
}

/** Clear the buffer + the persisted copy (DevTools-panel button). */
export function clearLogs(): void {
  buffer.length = 0;
  if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(BUFFER_KEY); } catch { /* */ }
  }
  // Notify with a synthetic entry so subscribers re-render their lists.
  push("info", "state", "logs cleared");
}

/** Toggle console mirroring; the panel keeps working either way. */
export function setConsoleEnabled(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (on) window.localStorage.setItem(STORAGE_FLAG, "1");
    else    window.localStorage.removeItem(STORAGE_FLAG);
  } catch { /* */ }
}

export function isConsoleEnabled(): boolean {
  return consoleEnabled();
}
